import { DEFAULT_REACTION_EXPORT_DIMENSIONS, REACTION_LAYOUT_BASE } from './exportLayout';

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
// Visual calibration so arrow/text scale better matches molecule apparent size.
// <1 shrinks arrow cluster relative to molecules.
const ARROW_CLUSTER_VISUAL_CALIBRATION = 0.78;

/**
 * Conservative width reserved for the arrow column + flex gaps when estimating
 * how wide each molecule column is. Must NOT depend on live measured arrow width
 * (that created a feedback loop: arrowWidth → moleculeWidth → metrics → clusterScale → text width → arrowWidth).
 */
const ARROW_COLUMN_RESERVE_PX = 112;

function parseViewBoxSize(viewBox: string | undefined): { w: number; h: number } | null {
  if (!viewBox) return null;
  const parts = viewBox.trim().split(/\s+/).map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [, , vbW, vbH] = parts;
  if (vbW <= 0 || vbH <= 0) return null;
  return { w: vbW, h: vbH };
}

/**
 * Scale for reaction arrow + reagent/condition labels so they track apparent
 * bond/atom size (viewBox, reactant pan-zoom) at the default reaction layout size,
 * without depending on the current measured arrow column width.
 *
 * Uses the same svgScale idea as InteractiveMolecule: min(molW/vbW, molH/vbH),
 * with molecule slot size estimated from **default** reaction export dimensions.
 * Live export width/height must not affect this scale — only viewBox + reactant zoom —
 * so resizing the canvas does not shrink/grow the arrow and labels.
 */
export function getStableReactionArrowClusterScale(
  reactantViewBox: string | undefined,
  reactantZoom: number
): number {
  const vb = parseViewBoxSize(reactantViewBox);
  if (!vb) {
    const calibrated = reactantZoom * ARROW_CLUSTER_VISUAL_CALIBRATION;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, calibrated));
  }

  const pad = REACTION_LAYOUT_BASE.padding;
  const gap = REACTION_LAYOUT_BASE.gap;
  const { width: refWpx, height: refHpx } = DEFAULT_REACTION_EXPORT_DIMENSIONS;
  const inner = refWpx - pad * 2;
  const contentHeight = Math.max(64, refHpx - pad * 2);

  const approxMoleculeWidth = Math.max(
    64,
    (inner - 2 * gap - ARROW_COLUMN_RESERVE_PX) / 2
  );

  const currentSvgScale = Math.min(approxMoleculeWidth / vb.w, contentHeight / vb.h);
  const refW = REACTION_LAYOUT_BASE.moleculeWidth;
  const refH = REACTION_LAYOUT_BASE.moleculeHeight;
  const referenceSvgScale = Math.min(refW / vb.w, refH / vb.h);

  if (referenceSvgScale <= 0) {
    const calibrated = reactantZoom * ARROW_CLUSTER_VISUAL_CALIBRATION;
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, calibrated));
  }

  const scale = ((currentSvgScale * reactantZoom) / referenceSvgScale) * ARROW_CLUSTER_VISUAL_CALIBRATION;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

